(() => {
  const selector = '[data-drapeur-load-more]';
  const stateKey = 'drapeurCollectionV1';
  const cacheKey = 'drapeurCollectionPagesV1';
  const cacheLifetime = 10 * 60 * 1000;
  const cacheLimit = 2000000;

  // Cache only uninitialised server cards, scoped to this tab and storefront context.
  // A matching history entry and fresh first-page signature are required for reuse.
  function cacheIdentity(current) {
    return JSON.stringify([current.key, current.root.dataset.sectionId, current.first,
      current.pageSize, current.root.dataset.total, document.documentElement.lang,
      window.Shopify?.country, window.Shopify?.currency?.active, window.Shopify?.theme?.id,
      [...current.grid.children].map(card => [card.dataset.productId, card.textContent.trim()])]);
  }

  function readCache(current, saved) {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(cacheKey));
      if (cached?.id === saved?.cacheId && cached.identity === current.identity
        && Number.isFinite(cached.created) && Date.now() - cached.created >= 0
        && Date.now() - cached.created < cacheLifetime && cached.pages && typeof cached.pages === 'object') {
        current.cache = cached;
        return cached;
      }
    } catch (_) {}
    return null;
  }

  function cachePage(current, html, page) {
    if (!current.cache || Date.now() - current.cache.created >= cacheLifetime) {
      current.cache = {id: Date.now() + '-' + Math.random().toString(36).slice(2),
        identity: current.identity, created: Date.now(), pages: {}};
    }
    current.cache.pages[page] = html;
    try {
      const value = JSON.stringify(current.cache);
      if (value.length > cacheLimit) {
        window.sessionStorage.removeItem(cacheKey);
        current.cache = null;
      } else window.sessionStorage.setItem(cacheKey, value);
    } catch (_) { /* Storage restrictions must never block collection navigation. */ }
  }

  function enhanceCards() {
    window.theme?.reinitProductGridItem?.();
    document.dispatchEvent(new CustomEvent('collection:appended'));
  }
  let active = null;
  let generation = 0;
  let controller = null;
  let saving = 0;
  let suspended = false;
  let restoring = false;
  let userMoved = false;
  let stopPositionWatch = null;

  function revealReturn() {
    window.DrapeurCollectionReturn?.release();
  }

  function anchorFor(current, card) {
    const cards = card ? [card] : [...current.grid.children];
    for (const item of cards) {
      const rect = item.getBoundingClientRect?.();
      if (rect && (card || rect.bottom > 0)) return { id: item.dataset.productId, offset: rect.top };
    }
    return null;
  }

  function pageKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return url.pathname + url.search;
  }

  function save(card) {
    clearTimeout(saving);
    if (!active || suspended || restoring || !active.root.isConnected || active.key !== pageKey()) return;
    try {
      history.replaceState({
        ...history.state,
        [stateKey]: {
          key: active.key,
          section: active.root.dataset.sectionId,
          first: active.first,
          last: active.last,
          y: window.scrollY,
          count: active.grid.children.length,
          pageSize: active.pageSize,
          cacheId: active.cache?.id,
          anchor: anchorFor(active, card)
        }
      }, '');
    } catch (_) {}
  }

  function setBusy(current, value) {
    current.busy = value;
    current.root.setAttribute('aria-busy', String(value));
    current.next.setAttribute('aria-disabled', String(value));
    current.next.querySelectorAll('[data-load-more-label]').forEach(label => {
      label.textContent = value ? current.root.dataset.loading : current.root.dataset.label;
    });
  }

  function cancel() {
    generation++;
    clearTimeout(saving);
    controller?.abort();
    controller = null;
    restoring = false;
    stopPositionWatch?.();
    if (active) setBusy(active, false);
  }

  function updateCount(current) {
    const count = current.grid.querySelectorAll(':scope > [data-product-id]').length;
    current.root.querySelector('[data-load-more-count]').textContent = current.root.dataset.counter
      .replace('__COUNT__', String(count)).replace('__TOTAL__', current.root.dataset.total);
    current.next.querySelectorAll('[data-load-more-fraction]').forEach(fraction => {
      fraction.textContent = current.root.dataset.fraction
        .replace('__COUNT__', String(count)).replace('__TOTAL__', current.root.dataset.total);
    });
    current.root.querySelector('[data-load-more-count]').classList.toggle('is-complete', current.next.hidden);
  }

  function nextURL(current) {
    const url = new URL(current.next.href, window.location.href);
    const base = new URL(window.location.href);
    if (url.origin !== base.origin || url.pathname !== base.pathname) throw new Error('Invalid collection URL');
    const page = Number(url.searchParams.get('page'));
    if (page !== current.last + 1) throw new Error('Unexpected page');
    for (const [key, value] of base.searchParams) {
      if (key === 'preview_theme_id' || key === '_fd' || key === 'pb') url.searchParams.set(key, value);
    }
    url.searchParams.set('section_id', current.root.dataset.sectionId);
    return url;
  }

  function applyPage(current, html, url, focusNew = false) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector(selector);
    const grid = doc.querySelector('.collection-grid__wrapper > .grid');
    if (!incoming || !grid || incoming.dataset.sectionId !== current.root.dataset.sectionId || Number(incoming.dataset.page) !== current.last + 1) throw new Error('Invalid collection response');
    const cards = [...grid.children];
    if (!cards.length || cards.some(card => !card.matches('[data-product-id]'))) throw new Error('Missing products');
    const existing = new Set([...current.grid.children].map(card => card.dataset.productId));
    const added = cards.filter(card => {
      if (existing.has(card.dataset.productId)) return false;
      existing.add(card.dataset.productId);
      return true;
    });
    const next = incoming.querySelector('[data-load-more-next]');
    if (!next) throw new Error('Missing pagination');
    const cachedHTML = '<div class="collection-grid__wrapper">' + grid.outerHTML + '</div>' + incoming.outerHTML;
    current.grid.append(...added);
    current.last = Number(incoming.dataset.page);
    current.root.dataset.page = incoming.dataset.page;
    current.root.dataset.total = incoming.dataset.total;
    current.next.hidden = next.hidden || !next.hasAttribute('href');
    if (!current.next.hidden) {
      const nextPage = new URL(next.getAttribute('href'), url);
      nextPage.searchParams.delete('section_id');
      current.next.href = nextPage.pathname + nextPage.search;
      current.root.querySelector('[data-load-more-fallback]').href = current.next.href;
    } else {
      current.next.removeAttribute('href');
    }
    const pagination = incoming.querySelector('[data-load-more-pagination]');
    if (pagination) current.root.querySelector('[data-load-more-pagination]').innerHTML = pagination.innerHTML;
    updateCount(current);
    if (!restoring) enhanceCards();
    if (focusNew && added.length) {
      const target = added[0].querySelector('.grid-product__link');
      target?.focus({ preventScroll: true });
    }
    return cachedHTML;
  }

  async function append(current, focusNew = false) {
    if (current.busy || current.next.hidden || !current.next.hasAttribute('href')) return false;
    const token = generation;
    const abort = new AbortController();
    controller = abort;
    const timeout = setTimeout(() => abort.abort(), 15000);
    setBusy(current, true);
    current.root.querySelector('[data-load-more-error]').hidden = true;
    try {
      const url = nextURL(current);
      const response = await fetch(url.href, { signal: abort.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Collection unavailable');
      const html = await response.text();
      if (token !== generation || current !== active || !current.root.isConnected || current.key !== pageKey()) return false;
      const cachedHTML = applyPage(current, html, url, focusNew);
      cachePage(current, cachedHTML, current.last);
      save();
      return true;
    } catch (_) {
      if (token === generation && current === active && current.root.isConnected) {
        current.root.querySelector('[data-load-more-error]').hidden = false;
      }
      return false;
    } finally {
      clearTimeout(timeout);
      if (controller === abort) controller = null;
      if (token === generation) setBusy(current, false);
    }
  }

  async function restore(current, saved) {
    stopPositionWatch?.();
    restoring = true;
    userMoved = false;
    const token = generation;
    let complete = true;
    const last = saved.pageSize && saved.pageSize !== current.pageSize && Number.isInteger(saved.count)
      ? current.first + Math.ceil(saved.count / current.pageSize) - 1 : saved.last;
    const cached = current.cache || readCache(current, saved);
    const startingPage = current.last;
    while (current.last < last && !current.next.hidden && token === generation) {
      const html = cached?.pages[current.last + 1];
      if (typeof html === 'string') {
        try {
          applyPage(current, html, nextURL(current));
          continue; // No await: restore all cached pages and position in the same task.
        } catch (_) { delete cached.pages[current.last + 1]; }
      }
      if (!await append(current)) {
        complete = false;
        break;
      }
    }
    if (token !== generation) return;
    if (current.last !== startingPage) enhanceCards();
    if (!complete || userMoved) {
      revealReturn();
      restoring = false;
      if (complete) save();
      return;
    }
    // An image, font or browser history restore can move the grid after its first paint.
    // Keep the product at its saved viewport offset until layout settles, or the user acts.
    let stopped = false;
    let observer;
    let timeout;
    const align = () => {
      if (stopped || userMoved || token !== generation || current !== active) return;
      const anchor = saved.anchor && Number.isFinite(saved.anchor.offset)
        ? [...current.grid.children].find(card => card.dataset.productId === saved.anchor.id) : null;
      const rect = anchor?.getBoundingClientRect?.();
      const top = rect ? window.scrollY + rect.top - saved.anchor.offset : saved.y;
      if (Math.abs(window.scrollY - Math.max(0, top)) > .5) {
        window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
      }
    };
    const stop = () => {
      if (stopped) return;
      stopped = true;
      clearTimeout(timeout);
      observer?.disconnect();
      document.removeEventListener('load', align, true);
      window.removeEventListener('scroll', align);
      if (stopPositionWatch === stop) stopPositionWatch = null;
      if (token === generation) {
        restoring = false;
        save();
      }
    };
    stopPositionWatch = stop;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(align);
      observer.observe(current.grid);
      observer.observe(document.body);
    }
    document.addEventListener('load', align, true);
    window.addEventListener('scroll', align, { passive: true });
    document.fonts?.ready.then(align);
    align();
    requestAnimationFrame(() => { align(); requestAnimationFrame(() => { align(); revealReturn(); }); });
    timeout = setTimeout(stop, 2000);
  }

  function init(allowRestore = false) {
    cancel();
    suspended = false;
    const root = document.querySelector(selector);
    const grid = root?.closest('[data-section-type="collection-grid"]')?.querySelector('.collection-grid__wrapper > .grid');
    active = null;
    if (!root || !grid) { revealReturn(); return; }
    const saved = history.state?.[stateKey];
    const page = Number(root.dataset.page);
    active = {
      root, grid, next: root.querySelector('[data-load-more-next]'),
      key: pageKey(), first: page, last: page, busy: false,
      pageSize: Number(root.dataset.pageSize) || grid.children.length
    };
    active.identity = cacheIdentity(active);
    root.querySelector('[data-load-more-controls]').hidden = false;
    root.querySelector('[data-load-more-pagination]').hidden = true;
    updateCount(active);
    const validSaved = allowRestore && saved?.key === active.key && saved.section === root.dataset.sectionId && saved.first === page
      && Number.isInteger(saved.last) && saved.last >= page && saved.last <= 25000
      && Number.isFinite(saved.y) && saved.y >= 0;
    if (validSaved) restore(active, saved);
    else { revealReturn(); save(); }
  }

  // Capture the clicked card before navigation, rather than relying on a delayed scroll save.
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    const card = link?.closest('[data-product-id]');
    if (!active || !card || card.parentElement !== active.grid || event.button !== 0
      || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link.target === '_blank') return;
    const url = new URL(link.getAttribute('href'), window.location.href);
    if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
    userMoved = true;
    stopPositionWatch?.();
    if (restoring) cancel();
    save(card);
    active.departure = history.state?.[stateKey];
  }, true);

  document.addEventListener('collection:history-restore', event => {
    const saved = history.state?.[stateKey];
    if (!active || active.key !== pageKey() || saved?.key !== active.key || saved.section !== active.root.dataset.sectionId) return;
    event.preventDefault();
    active.departure = null;
    restore(active, saved);
  });

  document.addEventListener('click', event => {
    const link = event.target.closest?.('[data-load-more-next]');
    if (!link || !active || link !== active.next || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (suspended || restoring || active.busy) return;
    append(active, event.detail === 0);
  });
  document.addEventListener('collection:before-render', () => {
    suspended = true;
    cancel();
  });
  document.addEventListener('collection:reloaded', event => init(event.detail?.restoreHistory === true));
  document.addEventListener('shopify:section:load', () => init());
  window.addEventListener('scroll', () => {
    clearTimeout(saving);
    saving = setTimeout(save, 150);
  }, { passive: true });
  for (const type of ['wheel', 'touchstart', 'keydown', 'pointerdown']) {
    window.addEventListener(type, () => {
      userMoved = true;
      revealReturn();
      stopPositionWatch?.();
      if (active) active.departure = null;
    }, { passive: true });
  }
  window.addEventListener('pagehide', () => {
    if (!active?.departure) save();
    else {
      clearTimeout(saving);
      history.replaceState({ ...history.state, [stateKey]: active.departure }, '');
    }
    cancel();
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted && active) {
      suspended = false;
      setBusy(active, false);
      active.departure = null;
      const saved = history.state?.[stateKey];
      if (saved?.key === active.key) restore(active, saved);
    }
  });
  // Start when the collection markup is available, without waiting for deferred theme scripts.
  let started = false;
  function start() {
    if (started) return;
    started = true;
    init(true);
  }
  if (document.querySelector('[data-load-more-pagination]')) start();
  else {
    document.addEventListener('drapeur:collection-ready', start, { once: true });
    document.addEventListener('DOMContentLoaded', start, { once: true });
  }
})();
